/**
 * Whether a 401 means the user has NO SESSION, as opposed to this one request not being allowed.
 * Getting that distinction wrong is what sent "Remember this device" users back to an email code
 * every morning, twice fixed server side and twice still broken, so the rule lives in one named
 * place rather than inline in the HTTP client.
 *
 * The rule: **the session is torn down when a refresh is actively rejected, and only then.**
 * `Session.context` owns that teardown and clears the stored tokens when the API refuses a refresh
 * — so "is a session still held after the refresh attempt?" is the whole question. It is asked
 * AFTER the attempt for exactly that reason, never inferred from the status code.
 *
 * What this deliberately does NOT treat as a dead session:
 *
 * - **A 401 that survives a successful refresh.** The session is demonstrably alive, it was just
 *   renewed, and the API is saying this profile may not call this route. Signing somebody out for
 *   asking the wrong question is not a safety measure, it is a bug.
 * - **A refresh that failed because the browser is offline or the API is down.** Those leave the
 *   stored tokens alone, so the session survives — and it must, because the API never saw the
 *   refresh token and never refused it. A laptop opened before its Wi-Fi reconnects is the single
 *   most common way this happens, which is why the sign-outs arrived daily and first thing.
 *
 * The 403 case is not represented here at all: a 403 never reaches this decision, because it is a
 * verdict on the request rather than on the session. See `isUnauthenticated` in the HTTP client.
 *
 * Mirrors `shouldRedirectToLogin` in DanceWorld.Mobile, which fixed the same class of bug there.
 */
export const shouldRedirectToLogin = (options: {
  /**
   * The caller's `autoLogOutOnAuthError` setting — some reads (the examiner surface, the public
   * invoice view, the OTP calls themselves) opt out entirely. Absent counts as opted out, matching
   * the request options this is fed from.
   */
  autoLogOutOnAuthError?: boolean;
  /** Whether a session is still held, read AFTER the refresh attempt. */
  hasLiveSession: boolean;
}): boolean => !!options.autoLogOutOnAuthError && !options.hasLiveSession;
